<script lang="ts">
	//Formating
	import { format } from 'svelte-i18n';
	//Design system components
	import {
		Text,
		Slider,
		Checkbox,
		Container,
		Loader,
		Alert,
		Button,
	} from '@Shelly/shelly-design-system';
	//Types
	import type { LocalControlOptions } from 'src/types/requests/schedules/schedules_g2';
	import { capFirstChar } from 'src/helpers/utilities';
	import type { Device } from 'src/types/Device';
	import SelectList from 'src/components/SelectList/SelectList.svelte';
	import {
		loadMediaFiles,
		type MediaFile,
	} from 'src/helpers/wall_display_extract/media_player_helpers';

	export let controlOption: LocalControlOptions, device: Device;

	let mediaFiles: MediaFile[] = null; //array of media files received from the device
	let errorLoadMediaFiles = false;

	const selectSong = (e: CustomEvent) => {
		controlOption.params.id = e.detail.id;
		mediaFiles = mediaFiles.map((m) => ({
			...m,
			selected: controlOption.params.id === m.id,
		}));
	};

	const loadFiles = async () => {
		const files = await loadMediaFiles(device);
		errorLoadMediaFiles = files === null;
		// The user can enter a custom ID — needs validation
		if (typeof controlOption.params.id !== 'number') {
			controlOption.params.id = 0;
		}
		const selectedFile = (files || []).find((m) => m.id === controlOption.params.id);
		if (selectedFile) {
			selectedFile.selected = true;
		} else if (files?.length) {
			files[0].selected = true;
			controlOption.params.id = files[0].id;
		}
		mediaFiles = files;
	};

	$: if (mediaFiles === null && controlOption?.value === 'media.mediaplayer.playaudioclip') {
		loadFiles();
	}
	const a11yPropsSliderVolume = {
		sliderValueText: $format('current_volume_value', {
			values: { value: controlOption.params.volume || 0 },
			default: `Current volume value is ${controlOption.params.volume || 0}`,
		}),
		ariaLabel: $format('label_set_volume_property', {
			default: 'Set Volume property',
		}),
		ariaLabelValue: $format('current_volume_value', {
			values: { value: controlOption.params.volume || 0 },
			default: `Current volume value is ${controlOption.params.volume || 0}`,
		}),
	};
</script>

<Checkbox size="m" class="mb-xs" bind:checked={controlOption.attr.volume} id="checkbox-tkljpod">
	{capFirstChar(
		$format('label_volume', {
			default: 'Volume',
		}),
	)}
</Checkbox>
<Text styleType="body-small" class="mb-s {controlOption.attr.volume ? '' : 'text-color-3'}">
	{$format('min_max_unit_range', {
		default: '(Minimum: {min}, Maximum: {max})',
		values: { min: 0, max: 100 },
	})}
</Text>
<Slider
	id="slider-dby1o2d"
	type="simple"
	class="mb-s"
	value={controlOption.params.volume || 0}
	disabled={!controlOption.attr.volume}
	min={0}
	unit="%"
	on:value_change={(e) => {
		controlOption.params.volume = e.detail.value;
	}}
	title={capFirstChar(
		$format('label_cover_slider_title_min_max_unit', {
			values: {
				pos: controlOption.params.volume || 0,
				min: 0,
				max: 100,
				unit: '%',
			},
			default: 'Selected: {pos}{unit}, Range: ( {min}{unit} - {max}{unit} )',
		}),
	)}
	a11yProps={a11yPropsSliderVolume}
/>

{#if controlOption?.value === 'media.mediaplayer.playaudioclip'}
	{#if errorLoadMediaFiles}
		<Alert
			type="danger"
			heading={$format('data_not_loaded', {
				default: "Couldn't load data",
			})}
			class="mt-s mb-s"
		/>
		<Button
			size="m"
			class="width-full mr-s text-capitalized"
			responsiveSize={{
				sm: 'm',
			}}
			variant="secondary"
			on:click={loadFiles}
		>
			{$format('button_retry', { default: 'Retry' })}
		</Button>
	{:else if mediaFiles === null}
		<div class="d-flex justify-content-center align-items-center">
			<Loader size="m" />
		</div>
	{:else if mediaFiles?.length > 0}
		<Container padding="0">
			<SelectList
				withSearch={true}
				variant={'radio'}
				title={$format('title_select_song', {
					default: 'Select song to play',
				})}
				list={mediaFiles}
				on:click={(e) => selectSong(e)}
			/>
		</Container>
	{:else}
		<Alert
			type="info"
			heading={$format('no_audio_files_uploaded_title', {
				default: 'No audio files uploaded.',
			})}
			class="mt-s mb-s"
		/>
	{/if}
{/if}
